import { useState, type ReactNode } from 'react';

interface Props {
  /** How many filters differ from the defaults, shown as a badge. */
  activeCount?: number;
  onReset?: () => void;
  /** Extra controls in the header, right-aligned (export, live toggle…). */
  actions?: ReactNode;
  /** Less-used filters, folded away until asked for. */
  more?: ReactNode;
  /** Active filters inside `more`, so a folded section never hides a live filter. */
  moreActiveCount?: number;
  children: ReactNode;
}

/**
 * The card every filter bar sits in: a header that says how many filters are
 * on and resets them, the everyday filters, and an optional fold for the rest.
 */
export function FilterPanel({ activeCount = 0, onReset, actions, more, moreActiveCount = 0, children }: Props) {
  const [open, setOpen] = useState(false);
  const showMore = open || moreActiveCount > 0;

  return (
    <section className="filters" aria-label="Filters">
      <div className="filters-head">
        <span className="filters-title">
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M2 3.5h12M4.5 8h7M7 12.5h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          Filters
          {activeCount ? <span className="badge-count">{activeCount}</span> : null}
        </span>
        <div className="row">
          {actions}
          {more ? (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              aria-expanded={showMore}
              disabled={moreActiveCount > 0}
              title={moreActiveCount > 0 ? 'Clear the extra filters to fold them away' : undefined}
              onClick={() => setOpen((value) => !value)}
            >
              {showMore ? 'Fewer filters' : 'More filters'}
              {moreActiveCount ? ` (${moreActiveCount})` : ''}
            </button>
          ) : null}
          {onReset && activeCount ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onReset}>
              Reset
            </button>
          ) : null}
        </div>
      </div>
      <div className="filters-body">{children}</div>
      {more && showMore ? <div className="filters-body filters-more">{more}</div> : null}
    </section>
  );
}

interface DateRangeProps {
  id: string;
  label: string;
  from: string;
  to: string;
  /** Latest selectable end date; omit for open-ended (future) ranges. */
  max?: string;
  presets?: { label: string; from: string; to: string }[];
  onChange: (from: string, to: string) => void;
  /** Shows a clear button when either end is set (for optional ranges). */
  clearable?: boolean;
}

/** Two dates read as one control, with quick ranges beside them. */
export function DateRangeField({ id, label, from, to, max, presets = [], onChange, clearable }: DateRangeProps) {
  const active = presets.find((preset) => preset.from === from && preset.to === to);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="row nowrap-row">
        <div className="date-range">
          <input
            id={id}
            type="date"
            value={from}
            max={to || max}
            onChange={(event) => onChange(event.target.value, to)}
          />
          <span className="date-range-sep" aria-hidden="true">
            →
          </span>
          <input
            type="date"
            aria-label={`${label} end`}
            value={to}
            min={from || undefined}
            max={max}
            onChange={(event) => onChange(from, event.target.value)}
          />
          {clearable && (from || to) ? (
            <button type="button" className="date-range-clear" aria-label="Clear dates" onClick={() => onChange('', '')}>
              ✕
            </button>
          ) : null}
        </div>
        {presets.length ? (
          <div className="segmented" role="group" aria-label={`${label} presets`}>
            {presets.map((preset) => (
              <button
                type="button"
                key={preset.label}
                aria-pressed={active?.label === preset.label}
                onClick={() => onChange(preset.from, preset.to)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A labelled on/off switch, for the yes/no filters. */
export function SwitchField({
  id,
  label,
  text,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  text: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <label className="switch" htmlFor={id}>
        <input id={id} type="checkbox" role="switch" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        {text}
      </label>
    </div>
  );
}
